import { defineQuery } from "@rocicorp/zero";
import { zql } from "../zql";

export const projectQueries = {
	// The board's left rail: every active project of the caller's organisation with its open
	// tickets, so a planner can drag work onto a column.
	//
	// The root is pinned to `ctx.orgId`; everything below it is reached through a foreign key, and
	// a foreign key is checked with row level security off. A junction row is therefore the whole
	// distance between "my ticket" and somebody else's file: a `ticketFiles` row pointing one of my
	// tickets at a foreign `files` row would deliver that file here. Every hop whose table carries
	// an organisation of its own compares it, so such a row yields nothing rather than a foreign
	// column of data.
	//
	// One hop carries no organisation and cannot be given one: `_members._user` (users), because a
	// person belongs to as many organisations as they have memberships. Every other table here has
	// an `orgId` and compares it. `queries/delivered-tables.test.ts` holds that list and turns red
	// when it changes.
	//
	// Each hop callback takes its type from the relationship it descends into, which is what makes
	// those clauses checkable at all: the compiler knows the destination table's columns, so a
	// mistyped column name is an error rather than a clause that quietly matches nothing.
	list: defineQuery(({ ctx }) => {
		return zql.projects
			.where("orgId", ctx.orgId)
			.where("state", "active")
			.related("_client", (contact) => contact.where("orgId", "=", ctx.orgId))
			.related("_workspace", (workspace) => workspace.where("orgId", "=", ctx.orgId))
			.whereExists("_tickets", (ticket) => ticket.where("state", "active"))
			.related("_tickets", (ticket) =>
				ticket
					.where("orgId", "=", ctx.orgId)
					.where("state", "active")
					.orderBy("number", "asc")
					.related("ticketFiles", (ticketFile) =>
						ticketFile
							.where("orgId", "=", ctx.orgId)
							.related("file", (file) => file.where("orgId", "=", ctx.orgId)),
					)
					.related("projectResourceLinks", (projectResourceLink) =>
						projectResourceLink
							.where("orgId", "=", ctx.orgId)
							.related("resourceLink", (resourceLink) =>
								resourceLink.where("orgId", "=", ctx.orgId),
							),
					),
			)
			.related("_members", (member) => member.where("orgId", "=", ctx.orgId).related("_user"))
			.related("_relatedProject", (relatedProject) =>
				relatedProject.where("orgId", "=", ctx.orgId),
			);
	}),
} as const;
